import React, { useState, useEffect, useCallback } from "react";
import { useAuth0 } from '@auth0/auth0-react';
import Select from 'react-select';
import { categoriesAPI, subjectsAPI, classroomsAPI, organizationsAPI } from '../../api';
import instructorsAPI from '../../api/instructorsAPI';

export default function ManageClassesModal({
  onClose,
  selectedSchool, // now receives the full object
  selectedCategory: propSelectedCategory,
  onAddClass, // for add class callback
  organization_id: proporganization_id
}) 
{
  const { getAccessTokenSilently } = useAuth0();
  
  // Hierarchy data
  const [categories, setCategories] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  
  // Selected items
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  
  // UI state
  const [menuOpenFor, setMenuOpenFor] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [renamedValue, setRenamedValue] = useState("");

  // New item inputs
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newSubjectName, setNewSubjectName] = useState("");
  const [newClassName, setNewClassName] = useState("");

  const [organization_id, setorganization_id] = useState(proporganization_id || null);
  const [schoolId, setSchoolId] = useState(selectedSchool?.id || null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Instructor options (loaded from API)
  const [instructorOptions, setInstructorOptions] = useState([]);
  const [instructorsById, setInstructorsById] = useState({});
  const [newInstructor, setNewInstructor] = useState(null);
  const [isJointMode, setIsJointMode] = useState(false);

  // Instructor change modal state
  const [showInstructorModal, setShowInstructorModal] = useState(false);
  const [classroomToUpdate, setClassroomToUpdate] = useState(null);
  const [selectedInstructor, setSelectedInstructor] = useState(null);
  const [changingInstructor, setChangingInstructor] = useState(false);
  const [instructors, setInstructors] = useState([]);

  // Load classrooms for a single subject
  const loadClassrooms = useCallback(async (subjectId) => {
    try {
      const token = await getAccessTokenSilently();
      // Pass admin_view=true to bypass teacher filtering in manage classes modal
      // Grab all classes for the school:
      const schoolClasses = await classroomsAPI.getAllClassrooms(token, null, schoolId, true); // true = admin_view
      // Filter to only those belonging to the clicked subject for this school:
      const filteredSchoolClasses = schoolClasses.filter(c => c.subject_id === subjectId);
      
      // Also get joint classes for this subject across the organization
      const allClasses = await classroomsAPI.getAllClassrooms(token, null, null, true); // true = admin_view
      const jointClasses = allClasses.filter(c => 
        c.subject_id === subjectId && 
        c.is_joint_class === true &&
        c.organization_id === organization_id
      );
      
      // Combine school-specific classes and joint classes
      const combinedClasses = [...filteredSchoolClasses, ...jointClasses];
      // Remove duplicates (in case a joint class was already included)
      const uniqueClasses = combinedClasses.filter((classroom, index, array) => 
        array.findIndex(c => c.id === classroom.id) === index
      );
      
      setClassrooms(uniqueClasses);
    } catch (error) {
      console.error('Error loading classrooms:', error);
      setClassrooms([]);
    }
  }, [getAccessTokenSilently, schoolId, organization_id]);

  // Load subjects for a category (without auto-selecting)
  const loadSubjects = useCallback(async (categoryId, orgIdOverride = organization_id) => {
    try {
      const token = await getAccessTokenSilently();
      // Pass admin_view=true to bypass teacher filtering in manage classes modal
      const subjectsData = await subjectsAPI.getAllSubjects(token, categoryId, schoolId, true); // true = admin_view
      
      // Subjects now come with classCount from the backend
      setSubjects(subjectsData);
      
      // Don't auto-select the first subject - let user click to choose
      setSelectedSubject(null);
      setClassrooms([]);
    } catch (error) {
      console.error('Error loading subjects:', error);
      // Don't block UI for subjects either - just set empty array
      setSubjects([]);
      setSelectedSubject(null);
      setClassrooms([]);
    }
  }, [getAccessTokenSilently, schoolId, organization_id]);

  // Load categories for the organization and selected school (without auto-selecting)
  const loadCategories = useCallback(async (orgId) => {
    try {
      const token = await getAccessTokenSilently();
      // Pass the JWT token as first parameter, schoolId as second, admin_view=true to see all categories
      const categoriesData = await categoriesAPI.getAllCategories(token, schoolId, true);
      
      // Filter out deleted categories as an extra safeguard (backend should already do this)
      const activeCategories = categoriesData.filter(category => 
        !category.deleted_at && category.is_active !== false
      );
      
      // Calculate subject counts for each category
      const categoriesWithCounts = await Promise.all(
        activeCategories.map(async (category) => {
          try {
            const subjectsInCategory = await subjectsAPI.getAllSubjects(token, category.id, schoolId, true);
            return {
              ...category,
              subjectCount: subjectsInCategory.length
            };
          } catch (error) {
            console.error(`Error loading subjects for category ${category.id}:`, error);
            return {
              ...category,
              subjectCount: 0
            };
          }
        })
      );
      
      setCategories(categoriesWithCounts);
      
      // Don't auto-select the first category - let user click to choose
      setSelectedCategory(null);
      setSelectedSubject(null);
      setSubjects([]);
      setClassrooms([]);
    } catch (error) {
      console.error('Error loading categories:', error);
      // Don't set a blocking error for categories - just log it and continue
      // This allows users to access the menu to create categories
      setCategories([]);
      setSubjects([]);
      setClassrooms([]);
    }
  }, [getAccessTokenSilently, schoolId]);

  // Load instructors for this organization (filtered by school if applicable)
  const loadInstructors = useCallback(async (orgId, schoolId = null) => {
    try {
      const token = await getAccessTokenSilently();
      // Fetch instructors for the org, filtered by school if provided
      console.log(`Loading instructors for org ${orgId}${schoolId ? ` and school ${schoolId}` : ''}`);
      const instructors = await instructorsAPI.getAllInstructors(token, { schoolId });
      const list = instructors || [];
      const options = list.map((i) => ({
        value: i.id,
        label: i.name || i.email || i.user_id || i.id,
      }));
      const byId = {};
      list.forEach(i => { byId[i.id] = i; });
      setInstructorOptions(options);
      setInstructorsById(byId);
      setInstructors(list); // Store raw instructor data for the modal
    } catch (err) {
      console.error('Error loading instructors:', err);
      setInstructorOptions([]);
      setInstructorsById({});
      setInstructors([]); // Clear instructor data on error
    }
  }, [getAccessTokenSilently]);

  // Load organization and data on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const token = await getAccessTokenSilently();
        
        // Use passed organization ID or get it from API
        let orgId = organization_id;
        if (!orgId) {
          const orgData = await organizationsAPI.getMyOrganization(token);
          const organization = Array.isArray(orgData) ? orgData[0] : orgData;
          orgId = organization.id;
          setorganization_id(orgId);
        }
        // Load instructors for this organization and school
        await loadInstructors(orgId, selectedSchool?.id);
        // Load categories for the selected school
        await loadCategories(orgId);
        
      } catch (error) {
        console.error('Error loading data:', error);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    if (selectedSchool?.id) {
      setSchoolId(selectedSchool.id);
      loadData();
    }
  }, [getAccessTokenSilently, organization_id, selectedSchool, loadInstructors, loadCategories]);

  // Handle category selection change
  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    loadSubjects(category.id);
  };

  // Handle subject selection change
  const handleSubjectChange = (subject) => {
    setSelectedSubject(subject);
    loadClassrooms(subject.id);
  };

  // Add new category
  const handleAddCategory = async () => {
    if (!newCategoryName.trim() || !schoolId) return;
    
    try {
      const token = await getAccessTokenSilently();
      const categoryData = {
        name: newCategoryName.trim(),
        school_id: schoolId, // Include the selected school ID
        order_index: categories.length
        // organization_id is now handled by JWT middleware
      };
      
      await categoriesAPI.createCategory(categoryData, token);
      setNewCategoryName('');
      await loadCategories(organization_id);
    } catch (error) {
      console.error('Error adding category:', error);
      alert('Failed to add category: ' + error.message);
    }
  };

  // Add new subject
  const handleAddSubject = async () => {
    if (!newSubjectName.trim() || !selectedCategory || !schoolId) return;
    
    try {
      const token = await getAccessTokenSilently();
      const subjectData = {
        name: newSubjectName.trim(),
        category_id: selectedCategory.id,
        school_id: schoolId, // Include the selected school ID
        order_index: subjects.length
        // organization_id is now handled by JWT middleware
      };
      
      await subjectsAPI.createSubject(subjectData, token);
      setNewSubjectName('');
      
      // Only reload subjects for the current category to stay on subjects view
      await loadSubjects(selectedCategory.id);
      
      // Update the subject count for the current category without changing selection
      const updatedCategories = categories.map(cat => 
        cat.id === selectedCategory.id 
          ? { ...cat, subjectCount: (cat.subjectCount || 0) + 1 }
          : cat
      );
      setCategories(updatedCategories);
    } catch (error) {
      console.error('Error adding subject:', error);
      alert('Failed to add subject: ' + error.message);
    }
  };

  // Add new class
  const handleAddClass = async () => {
    if (!newClassName.trim() || !selectedSubject) return;
    try {
      const token = await getAccessTokenSilently();
      const payload = {
        name: newClassName.trim(),
        school_id: isJointMode ? null : selectedSchool?.id, // Joint classes don't belong to a specific school
        subject_id: selectedSubject?.id,
        synced_teacher_id: newInstructor?.value || null,
        organization_id: organization_id || null,
        category_id: selectedCategory?.id,
        is_joint_class: isJointMode // Add joint class flag
      };
      const newClass = await classroomsAPI.createClassroom(payload, token);

      // Refresh the classroom list to include the new class
      await loadClassrooms(selectedSubject.id);
      
      // Update the class count for the current subject without changing navigation
      const updatedSubjects = subjects.map(subj => 
        subj.id === selectedSubject.id 
          ? { ...subj, classCount: (subj.classCount || 0) + 1 }
          : subj
      );
      setSubjects(updatedSubjects);

      // Optionally, show a toast if you have one, or just reset state
      // toast.success("Classroom added!");
      setNewClassName("");
      setNewInstructor(null);
      setIsJointMode(false); // Reset joint mode after adding
      if (typeof onAddClass === "function") {
        await onAddClass(newClass);
      }
    } catch (error) {
      console.error("Failed to add classroom:", error);
      // toast.error("Something went wrong");
      alert('Failed to add class: ' + error.message);
    }
  };

  // Delete subject
  const handleDeleteSubject = async (subject) => {
    if (!window.confirm(`Delete subject "${subject.name}"?`)) return;
    
    try {
      const token = await getAccessTokenSilently();
      await subjectsAPI.deleteSubject(subject.id, token);
      // Refresh both categories (to update subject counts) and subjects
      await loadCategories(organization_id);
      await loadSubjects(selectedCategory.id);
    } catch (error) {
      console.error('Error deleting subject:', error);
      alert('Failed to delete subject: ' + error.message);
    }
  };

  // Delete class
  const handleDeleteClass = async (classroom) => {
    if (!window.confirm(`Delete class "${classroom.name}"?`)) return;
    
    try {
      const token = await getAccessTokenSilently();
      await classroomsAPI.deleteClassroom(classroom.id, token);
      await loadClassrooms(selectedSubject.id);
      // Also refresh subjects to update class counts
      await loadSubjects(selectedCategory.id);
    } catch (error) {
      console.error('Error deleting class:', error);
      alert('Failed to delete class: ' + error.message);
    }
  };

  // Delete category
  const handleDeleteCategory = async (categoryToDelete = selectedCategory) => {
    if (!categoryToDelete) {
      console.error('No category provided for deletion');
      return;
    }
    
    console.log('Attempting to delete category:', categoryToDelete);
    
    const confirmMessage = `Are you sure you want to delete the category "${categoryToDelete.name}"?\n\nThis will also delete all subjects and classes within this category. This action cannot be undone.`;
    
    if (!window.confirm(confirmMessage)) return;
    
    try {
      const token = await getAccessTokenSilently();
      console.log('Calling categoriesAPI.deleteCategory with:', { id: categoryToDelete.id, token: token ? 'present' : 'missing' });
      
      await categoriesAPI.deleteCategory(categoryToDelete.id, token);
      console.log('Category deleted successfully, reloading categories...');
      
      await loadCategories(organization_id);
      console.log('Categories reloaded');
      
      // Reset selection if the deleted category was currently selected
      if (selectedCategory && selectedCategory.id === categoryToDelete.id) {
        setSelectedCategory(null);
        setSelectedSubject(null);
        setSubjects([]);
        setClassrooms([]);
        console.log('Reset category selection');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Failed to delete category: ' + error.message);
    }
  };

  // Rename subject
  const handleRenameSubject = async (subject, newName) => {
    if (!newName.trim()) return;
    
    try {
      const token = await getAccessTokenSilently();
      await subjectsAPI.updateSubject(subject.id, { name: newName.trim() }, token);
      await loadSubjects(selectedCategory.id);
    } catch (error) {
      console.error('Error renaming subject:', error);
      alert('Failed to rename subject: ' + error.message);
    }
  };

  // Rename class
  const handleRenameClass = async (classroom, newName) => {
    if (!newName.trim()) return;
    
    try {
      const token = await getAccessTokenSilently();
      await classroomsAPI.updateClassroom(classroom.id, { name: newName.trim() }, token);
      await loadClassrooms(selectedSubject.id);
    } catch (error) {
      console.error('Error renaming class:', error);
      alert('Failed to rename class: ' + error.message);
    }
  };

  // Change instructor
  const handleChangeInstructor = async (classroom, newInstructorId) => {
    try {
      const token = await getAccessTokenSilently();
      // Use updateClassroom with synced_teacher_id instead of assignInstructor
      await classroomsAPI.updateClassroom(classroom.id, { 
        synced_teacher_id: newInstructorId 
      }, token);
      await loadClassrooms(selectedSubject.id);
    } catch (error) {
      console.error('Error changing instructor:', error);
      alert('Failed to change instructor: ' + error.message);
    }
  };

  // Handle update subject
  const handleUpdateSubject = async () => {
    if (!renamedValue.trim() || !editingItem?.id) return;
    
    try {
      const subject = subjects.find(s => s.id === editingItem.id);
      if (subject) {
        await handleRenameSubject(subject, renamedValue);
        setEditingItem(null);
        setRenamedValue('');
      }
    } catch (error) {
      console.error('Error updating subject:', error);
      alert('Failed to update subject: ' + error.message);
    }
  };

  // Handle update classroom
  const handleUpdateClassroom = async () => {
    if (!renamedValue.trim() || !editingItem?.id) return;
    
    try {
      const classroom = classrooms.find(c => c.id === editingItem.id);
      if (classroom) {
        await handleRenameClass(classroom, renamedValue);
        setEditingItem(null);
        setRenamedValue('');
      }
    } catch (error) {
      console.error('Error updating classroom:', error);
      alert('Failed to update classroom: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center">Loading...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center text-red-600">{error}</div>
          <button 
            onClick={onClose}
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  if (loading) return <div>Loading...</div>;

  const renderMenu = ({ type, id, name }) => (
    <div className="absolute right-0 top-full mt-1 bg-white border rounded-lg shadow-xl z-[9999] min-w-48 max-w-xs">
      {type === 'subject' && (
        <>
          <button
            className="block w-full text-left px-4 py-2 hover:bg-gray-100 rounded-t-lg"
            onClick={() => { setMenuOpenFor(null); setEditingItem({ type, id, name }); setRenamedValue(name); }}
          >
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Rename Subject
            </div>
          </button>
          <button
            className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-red-600 rounded-b-lg"
            onClick={() => { setMenuOpenFor(null); handleDeleteSubject(subjects.find(s => s.id === id)); }}
          >
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete Subject
            </div>
          </button>
        </>
      )}
      {type === 'class' && (
        <>
          <button
            className="block w-full text-left px-4 py-2 hover:bg-gray-100 rounded-t-lg"
            onClick={() => { setMenuOpenFor(null); setEditingItem({ type, id, name }); setRenamedValue(name); }}
          >
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Rename Class
            </div>
          </button>
          <button
            className="block w-full text-left px-4 py-2 hover:bg-gray-100"
            onClick={() => { 
              setMenuOpenFor(null); 
              setClassroomToUpdate(classrooms.find(c => c.id === id)); 
              setShowInstructorModal(true); 
            }}
          >
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Change Instructor
            </div>
          </button>
          <button
            className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-red-600 rounded-b-lg"
            onClick={() => { setMenuOpenFor(null); handleDeleteClass(classrooms.find(c => c.id === id)); }}
          >
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete Class
            </div>
          </button>
        </>
      )}
    </div>
  );

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50"
      onClick={(e) => {
        // Close modal if clicking on backdrop (not on the modal content)
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col relative">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Manage Classes</h1>
            <p className="text-sm text-gray-600 mt-1">
              Organize your subjects and classes for {selectedSchool?.name}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold leading-none"
            aria-label="Close"
          >×</button>
        </div>

        {/* Navigation Breadcrumbs */}
        <div className="px-6 py-3 bg-gray-50 border-b">
          <div className="flex items-center space-x-2 text-sm">
            <button
              onClick={() => {
                setSelectedCategory(null);
                setSelectedSubject(null);
                setSubjects([]);
                setClassrooms([]);
              }}
              className={`px-3 py-1 rounded-full transition ${
                !selectedCategory 
                  ? 'bg-blue-100 text-blue-700 font-medium' 
                  : 'text-gray-600 hover:bg-gray-200'
              }`}
            >
              Categories
            </button>
            
            {selectedCategory && (
              <>
                <span className="text-gray-400">→</span>
                <button
                  onClick={() => {
                    setSelectedSubject(null);
                    setClassrooms([]);
                  }}
                  className={`px-3 py-1 rounded-full transition ${
                    selectedCategory && !selectedSubject 
                      ? 'bg-blue-100 text-blue-700 font-medium' 
                      : 'text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {selectedCategory.name}
                </button>
              </>
            )}
            
            {selectedSubject && (
              <>
                <span className="text-gray-400">→</span>
                <span className="px-3 py-1 bg-blue-100 text-blue-700 font-medium rounded-full">
                  {selectedSubject.name}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-hidden">
          {!selectedCategory ? (
            /* Categories View */
            <div className="h-full p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold">Subject Categories</h2>
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    placeholder="New category name..."
                    value={newCategoryName}
                    onChange={e => setNewCategoryName(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onKeyDown={e => e.key === 'Enter' && handleAddCategory()}
                  />
                  <button
                    onClick={handleAddCategory}
                    disabled={!newCategoryName.trim()}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                    </svg>
                    Add Category
                  </button>
                </div>
              </div>
              
              <div className="overflow-y-auto max-h-[50vh] pr-2">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {categories.map(category => (
                  <div
                    key={category.id}
                    className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow cursor-pointer group"
                    onClick={() => handleCategoryChange(category)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition">
                          {category.name}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {category.subjectCount || 0} subjects
                        </p>
                      </div>
                      <div className="relative">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setMenuOpenFor(menuOpenFor === `cat-${category.id}` ? null : `cat-${category.id}`);
                          }}
                          className="text-gray-400 hover:text-gray-600 p-1"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                          </svg>
                        </button>
                        {menuOpenFor === `cat-${category.id}` && (
                          <div className="absolute right-0 top-8 bg-white border rounded-lg shadow-lg z-[9999] min-w-48">
                            <button
                              className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-red-600"
                              onClick={(e) => {
                                e.stopPropagation();
                                setMenuOpenFor(null);
                                handleDeleteCategory(category);
                              }}
                            >
                              Delete Category
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="mt-4 flex items-center text-blue-600 text-sm font-medium">
                      View Subjects
                      <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {categories.length === 0 && (
                <div className="text-center py-12">
                  <div className="text-gray-400 mb-4">
                    <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" strokeWidth="1" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Categories Yet</h3>
                  <p className="text-gray-600 mb-4">Create your first subject category to get started.</p>
                </div>
              )}
            </div>
          ) : !selectedSubject ? (
            /* Subjects View */
            <div className="h-full p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold">Subjects in {selectedCategory.name}</h2>
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    placeholder="New subject name..."
                    value={newSubjectName}
                    onChange={e => setNewSubjectName(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onKeyDown={e => e.key === 'Enter' && handleAddSubject()}
                  />
                  <button
                    onClick={handleAddSubject}
                    disabled={!newSubjectName.trim()}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                    </svg>
                    Add Subject
                  </button>
                </div>
              </div>
              
              <div className="overflow-y-auto max-h-[50vh] pr-2">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {subjects.map(subject => {
                  const isEditing = editingItem?.type === 'subject' && editingItem.id === subject.id;
                  return (
                    <div
                      key={subject.id}
                      className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow group"
                    >
                      {isEditing ? (
                        <div className="space-y-3">
                          <input
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={renamedValue}
                            onChange={e => setRenamedValue(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleUpdateSubject()}
                          />
                          <div className="flex gap-2">
                            <button
                              className="flex-1 bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm"
                              onClick={handleUpdateSubject}
                            >
                              Save
                            </button>
                            <button 
                              className="flex-1 bg-gray-200 text-gray-700 px-3 py-1.5 rounded-lg text-sm"
                              onClick={() => setEditingItem(null)}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start justify-between">
                          <div 
                            className="flex-1 cursor-pointer"
                            onClick={() => handleSubjectChange(subject)}
                          >
                            <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition">
                              {subject.name}
                            </h3>
                            <p className="text-sm text-gray-500 mb-4">
                              {subject.classCount || 0} classes
                            </p>
                            <div className="flex items-center text-blue-600 text-sm font-medium">
                              View Classes
                              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                              </svg>
                            </div>
                          </div>
                          <div className="relative">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setMenuOpenFor(menuOpenFor === `subject-${subject.id}` ? null : `subject-${subject.id}`);
                              }}
                              className="text-gray-400 hover:text-gray-600 p-1"
                            >
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                              </svg>
                            </button>
                            {menuOpenFor === `subject-${subject.id}` && renderMenu({ type: 'subject', id: subject.id, name: subject.name })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                </div>
              </div>
              
              {subjects.length === 0 && (
                <div className="text-center py-12">
                  <div className="text-gray-400 mb-4">
                    <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" strokeWidth="1" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Subjects Yet</h3>
                  <p className="text-gray-600 mb-4">Add your first subject to start organizing classes.</p>
                </div>
              )}
            </div>
          ) : (
            /* Classes View */
            <div className="h-full p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold">Classes for {selectedSubject.name}</h2>
              </div>
              
              {/* Main Content Grid - Add New Class and Classes List Side by Side */}
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 h-[calc(100vh-220px)]">
                {/* Add New Class Form - Left Column */}
                <div className="lg:col-span-1">
                  <div className="bg-gray-50 rounded-xl p-4 h-fit sticky top-0">
                    <h3 className="text-lg font-medium mb-4">Add New Class</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Class Name
                          {isJointMode && (
                            <span className="ml-2 text-xs text-purple-600 font-normal">
                              (Joint Class)
                            </span>
                          )}
                        </label>
                        <input
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          type="text"
                          placeholder={isJointMode ? "Joint class name" : "Class name"}
                          value={newClassName}
                          onChange={e => setNewClassName(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && handleAddClass()}
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Assign Instructor</label>
                        <Select
                          isClearable
                          options={instructorOptions}
                          value={newInstructor}
                          onChange={setNewInstructor}
                          placeholder="Select instructor..."
                          className="text-sm"
                        />
                      </div>
                    </div>
                    
                    {/* Joint Class Toggle */}
                    <div className="mt-4 p-3 bg-white rounded-lg border">
                      <div className="flex items-center gap-3 mb-2">
                        <input
                          id="jointToggle"
                          type="checkbox"
                          checked={isJointMode}
                          onChange={() => setIsJointMode(j => !j)}
                          className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                        />
                        <label htmlFor="jointToggle" className="text-sm font-medium text-gray-700">
                          Joint Class Mode
                        </label>
                      </div>
                      <div className="text-xs text-gray-600">
                        {isJointMode ? (
                          <div className="text-purple-700 bg-purple-50 p-2 rounded border">
                            <strong>Joint Class:</strong> Students from any school can join.
                          </div>
                        ) : (
                          <div>
                            Allow students from any school in your organization to join.
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <button
                      className="w-full mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      onClick={handleAddClass}
                      disabled={!newClassName.trim()}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                      </svg>
                      Add Class
                    </button>
                  </div>
                </div>

                <div className="lg:col-span-3 overflow-y-auto">
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 pr-2">
                    {classrooms.map(classroom => {
                      const isEditing = editingItem?.type === 'class' && editingItem.id === classroom.id;
                      const isJoint = classroom.is_joint_class;
                      const instrId = classroom.synced_teacher_id || classroom.instructor_id || classroom.instructor;
                      const instructorLabel = instrId
                        ? (instructorsById[instrId]?.name || instructorsById[instrId]?.email || instrId)
                        : 'No instructor assigned';
                        
                      return (
                        <div
                          key={classroom.id}
                          className={`rounded-xl p-4 border ${
                            isJoint ? 'bg-purple-50 border-purple-200' : 'bg-white border-gray-200'
                          } hover:shadow-md transition-shadow relative overflow-visible`}
                        >
                          {isEditing ? (
                            <div className="space-y-3">
                              <input
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                value={renamedValue}
                                onChange={e => setRenamedValue(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleUpdateClassroom()}
                              />
                              <div className="flex gap-2">
                                <button
                                  className="flex-1 bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm"
                                  onClick={handleUpdateClassroom}
                                >
                                  Save
                                </button>
                                <button 
                                  className="flex-1 bg-gray-200 text-gray-700 px-3 py-1.5 rounded-lg text-sm"
                                  onClick={() => setEditingItem(null)}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex flex-col h-full">
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <h4 className="font-semibold text-gray-900">{classroom.name}</h4>
                                    {isJoint && (
                                      <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full">
                                        Joint
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="relative">
                                  <button
                                    onClick={() => setMenuOpenFor(menuOpenFor === `class-${classroom.id}` ? null : `class-${classroom.id}`)}
                                    className="text-gray-400 hover:text-gray-600 p-1"
                                  >
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                      <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                                    </svg>
                                  </button>
                                  {menuOpenFor === `class-${classroom.id}` && renderMenu({ type: 'class', id: classroom.id, name: classroom.name })}
                                </div>
                              </div>
                              <div className="text-sm text-gray-600 mt-auto">
                                <div className="flex items-center gap-1">
                                  <span>👨‍🏫</span>
                                  <span className="truncate">{instructorLabel}</span>
                                </div>
                                {isJoint && (
                                  <div className="text-purple-600 mt-1 text-xs">🌐 Organization-wide</div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  {classrooms.length === 0 && (
                    <div className="text-center py-12">
                      <div className="text-gray-400 mb-4">
                        <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" strokeWidth="1" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No Classes Yet</h3>
                      <p className="text-gray-600 mb-4">Create your first class for {selectedSubject.name}.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Instructor Selection Modal */}
      {showInstructorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-h-96 overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Select Instructor</h3>
              <button
                onClick={() => {
                  setShowInstructorModal(false);
                  setClassroomToUpdate(null);
                  setSelectedInstructor(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            
            {instructors.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No instructors available</p>
            ) : (
              <div className="space-y-2">
                {instructors.map((instructor) => (
                  <button
                    key={instructor.id}
                    onClick={() => setSelectedInstructor(instructor)}
                    className={`w-full text-left p-3 rounded-lg border transition-colors ${
                      selectedInstructor?.id === instructor.id
                        ? 'bg-blue-50 border-blue-200 text-blue-900'
                        : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    <div className="font-medium">
                      {instructor.first_name && instructor.last_name 
                        ? `${instructor.first_name} ${instructor.last_name}`
                        : instructor.name || instructor.email || `Instructor ${instructor.id}`
                      }
                    </div>
                    {instructor.email && (
                      <div className="text-sm text-gray-600">{instructor.email}</div>
                    )}
                  </button>
                ))}
              </div>
            )}
            
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowInstructorModal(false);
                  setClassroomToUpdate(null);
                  setSelectedInstructor(null);
                }}
                className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (selectedInstructor && classroomToUpdate) {
                    setChangingInstructor(true);
                    try {
                      await handleChangeInstructor(classroomToUpdate, selectedInstructor.id);
                      setShowInstructorModal(false);
                      setClassroomToUpdate(null);
                      setSelectedInstructor(null);
                    } catch (error) {
                      console.error('Error updating instructor:', error);
                    } finally {
                      setChangingInstructor(false);
                    }
                  }
                }}
                disabled={!selectedInstructor || changingInstructor}
                className={`px-4 py-2 rounded-lg ${
                  !selectedInstructor || changingInstructor
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {changingInstructor ? 'Updating...' : 'Update Instructor'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}